import React, { useState, useRef, useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TourV2ItineraryBreakdown from './TourV2ItineraryBreakdown'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import { arrayToObject } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import { printModal } from 'components/utils/printModal'
import LinePrintIcon from 'LuxKit/Icons/line/LinePrintIcon'
import Group from 'components/utils/Group'
import LineExcursionIcon from 'LuxKit/Icons/line/LineExcursionIcon'
import { rem } from 'polished'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import noop from 'lib/function/noop'

const AlternativeRouteCTA = styled(Group)`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

interface Props {
  offerType: App.Tours.V2OfferType
  itinerary: Array<App.Tours.TourV2OfferItineraryItem>
  showPrint?: boolean
  title?: string
  subtitle?: string
  showExperiences?: boolean
  alternativeVariation?: App.Tours.TourV2OfferVariation
  changeAlternativeVariation?: () => void
}

function TourV2Itinerary({
  itinerary,
  title,
  offerType,
  subtitle,
  alternativeVariation,
  showPrint = false,
  showExperiences = true,
  changeAlternativeVariation = noop,
}: Props) {
  const intl = useIntl()
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>(
    () => arrayToObject(itinerary, item => `${item.startDay}`, (item, index) => index === 0),
  )

  const expandAll = useMemo(() => {
    return Object.values(expandedAccordions).some(item => !item)
  }, [expandedAccordions])

  const toggleExpandAll = useCallback(() => {
    setExpandedAccordions(itinerary.reduce((obj, item) => Object.assign(obj, { [item.startDay]: expandAll }), {}))
  }, [expandAll, itinerary])

  const toggleAccordion = useCallback((key: string) => {
    setExpandedAccordions(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const itineraryPdfRef = useRef<HTMLDivElement | null>(null)

  const handleDownloadPdf = useCallback(async() => {
    // Wait expanded all accordions before generate/download the pdf
    await new Promise<void>(resolve => {
      setExpandedAccordions(() => {
        resolve()
        return itinerary.reduce((obj, item) => Object.assign(obj, { [item.startDay]: true }), {})
      })
    })
    if (itineraryPdfRef.current) printModal(itineraryPdfRef.current)
  }, [itinerary])

  const {
    hasTheSameStartAndEndLocation,
    startLocation,
    endLocation,
  } = useTourV2VariationDetails(alternativeVariation)

  const expandAllText = useMemo(() => {
    if (expandAll) {
      return intl.formatMessage({
        id: 'm35yqZMhqn',
        defaultMessage: 'Expand all',
        description: 'Button text to expand all itinerary items',
      })
    }
    return intl.formatMessage({
      id: 'Bc3cSjWj1D',
      defaultMessage: 'Hide all',
      description: 'Button text to hide all itinerary items',
    })
  }, [expandAll, intl])

  const alternativeRouteTitle = useMemo(() => {
    if (hasTheSameStartAndEndLocation) {
      return intl.formatMessage({
        id: 'dUSN4bwMi/',
        defaultMessage: 'Interested in an itinerary in reverse?',
        description: 'Heading for alternative route with same start and end location',
      })
    }
    return intl.formatMessage(
      {
        id: 'hbbGmIhJPh',
        defaultMessage: 'Interested in starting from {location} instead?',
        description: 'Heading for alternative route with different start location',
      },
      { location: startLocation },
    )
  }, [hasTheSameStartAndEndLocation, startLocation, intl])

  const alternativeRouteDescription = useMemo(() => {
    if (hasTheSameStartAndEndLocation) {
      return intl.formatMessage({
        id: '0vjFNBZT1W',
        defaultMessage: 'Experience an alternative route for this tour.',
        description: 'Description for alternative route with same start and end',
      })
    }
    return intl.formatMessage(
      {
        id: 'SkXk1BllNC',
        defaultMessage: 'Experience an alternative route for this tour, travelling from {startLocation} to {endLocation}.',
        description: 'Description for alternative route with different locations',
      },
      { startLocation, endLocation },
    )
  }, [hasTheSameStartAndEndLocation, startLocation, endLocation, intl])

  return <Group direction="vertical" gap={32} ref={itineraryPdfRef}>
    {!!subtitle && (
      <div className="subtitle-area">
        <BodyText
          variant="medium"
          colour="neutral-two"
          as="p"
          id="LUXKIT-MODAL-HEADER-SUBTITLE"
        >
          {subtitle}
        </BodyText>
      </div>
    )}
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      {!!title && <Heading variant="heading3" align="center">{title}</Heading>}
      {showPrint && (
        <TextButton className="hidden-print" kind="tertiary" onClick={handleDownloadPdf} startIcon={<LinePrintIcon />} horizontalOutdent="start">
          {intl.formatMessage({
            id: 'dHj7NsApK0',
            defaultMessage: 'Print',
            description: 'Button text to print itinerary',
          })}
        </TextButton>
      )}
      <TextButton className="hidden-print" kind="tertiary" horizontalOutdent="end" onClick={toggleExpandAll}>
        {expandAllText}
      </TextButton>
    </Group>
    <Group direction="vertical" gap={0} tabletGap={16}>
      {itinerary.map((item, index) => (
        <TourV2ItineraryBreakdown
          key={item.startDay}
          last={index === itinerary.length - 1}
          itineraryItem={item}
          open={expandedAccordions[item.startDay]}
          toggleAccordion={toggleAccordion}
          shouldDisplayLocationsVisited={offerType !== 'direct_tour'}
          showExperiences={showExperiences}
        />
      ))}
    </Group>
    {alternativeVariation && (
      <AlternativeRouteCTA direction="vertical" desktopDirection="horizontal" gap={12} horizontalAlign="space-between">
        <LineExcursionIcon size="XL" />
        <Group direction="vertical" gap={4}>
          <Heading variant="heading5">
            {alternativeRouteTitle}
          </Heading>
          <BodyText variant="medium" colour="neutral-two">
            {alternativeRouteDescription}
          </BodyText>
        </Group>
        <TextButton kind="secondary" onClick={changeAlternativeVariation}>
          {intl.formatMessage({
            id: 'tqVMOui+Fa',
            defaultMessage: 'View alternate route',
            description: 'Button text to view alternative tour route',
          })}
        </TextButton>
      </AlternativeRouteCTA>
    )}
  </Group>
}

export default TourV2Itinerary
